import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { useAuth } from './auth/AuthProvider'
import { useAuditLog, AUDIT_ACTIONS } from '../services/auditLog'
import RolePermissions from './RolePermissions'
import { portalRoleForGroups, PORTAL_ROLES } from '../config/rolePermissions'

const UserManagementIcon = styled.button`
  position: relative;
  height: 40px;
  padding: 0 1.25rem;
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  z-index: ${theme.zIndex.navigation};
  box-shadow: 0 2px 8px ${theme.color.shadow.dark};
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #b8002b;
    transform: translateY(-1px);
  }
`

const IconBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  background: #ffc107;
  color: #333;
  font-size: 0.65rem;
  font-weight: 700;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #1a1a1a;
`

const Modal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background: ${theme.color.background.light};
  border-radius: 8px;
  width: calc(100% - 2rem);
  max-width: 1100px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 8px 32px ${theme.color.shadow.dark};
`

const ModalHeader = styled.div`
  background: ${theme.color.accent.primary};
  color: ${theme.color.text.light};
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 1.6rem;
  font-weight: 600;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.color.text.light};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`

const ModalBody = styled.div`
  padding: 1.5rem;
  max-height: calc(90vh - 80px);
  overflow-y: auto;
`

const SectionDivider = styled.div`
  border-top: 1px solid #444;
  margin: 2rem 0;
`

const AccessHeading = styled.div`
  margin-bottom: 1.25rem;

  h4 {
    color: ${theme.color.text.base};
    font-size: 1.2rem;
    margin: 0 0 0.4rem;
  }

  p {
    color: ${theme.color.text.grey};
    font-size: 0.85rem;
    line-height: 1.45;
    margin: 0;
  }
`

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`

const Label = styled.label`
  display: block;
  color: ${theme.color.text.base};
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #444;
  border-radius: 4px;
  font-size: 1rem;
  background: ${theme.color.background.dark};
  color: ${theme.color.text.base};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
  }

  &::placeholder {
    color: ${theme.color.text.grey};
  }
`

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' && `
    background: ${theme.color.accent.primary};
    color: white;
    &:hover { background: #b8002e; }
  `}

  ${props => props.variant === 'secondary' && `
    background: #444;
    color: white;
    &:hover { background: #555; }
  `}

  ${props => props.variant === 'danger' && `
    background: #dc3545;
    color: white;
    &:hover { background: #c82333; }
  `}

  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`

const UserCard = styled.div`
  background: ${theme.color.background.dark};
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`

const UserInfo = styled.div`
  margin-bottom: 1rem;
`

const UserName = styled.h4`
  margin: 0 0 0.5rem 0;
  color: ${theme.color.text.base};
  font-size: 1.2rem;
`

const UserEmail = styled.p`
  margin: 0;
  color: ${theme.color.text.grey};
  font-size: 0.9rem;
`

const UserStatus = styled.span<{ status: string }>`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.5rem;
  background: ${props => props.status === 'ACTIVE' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 193, 7, 0.2)'};
  color: ${props => props.status === 'ACTIVE' ? '#4caf50' : '#ffc107'};
`

const GroupsSection = styled.div`
  margin-top: 1rem;
`

const GroupsTitle = styled.h5`
  margin: 0 0 0.75rem 0;
  color: ${theme.color.text.grey};
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const GroupsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`

const GroupBadge = styled.span<{ $isActive?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  font-size: 0.85rem;
  background: ${props => props.$isActive ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$isActive ? '#4caf50' : theme.color.text.grey};
  border: 1px solid ${props => props.$isActive ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
`

const RemoveGroupButton = styled.button`
  background: none;
  border: none;
  color: #ff6b6b;
  font-size: 1rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #ff4444;
  }
`

const AddGroupSection = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const Select = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid #444;
  border-radius: 4px;
  font-size: 0.9rem;
  background: ${theme.color.background.dark};
  color: ${theme.color.text.base};
  cursor: pointer;
  flex: 1;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: ${theme.color.accent.primary};
  }
`

const Message = styled.div<{ type: 'success' | 'error' | 'info' }>`
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  text-align: center;

  ${props => props.type === 'success' && `
    background: rgba(76, 175, 80, 0.1);
    color: #4caf50;
    border: 1px solid rgba(76, 175, 80, 0.3);
  `}

  ${props => props.type === 'error' && `
    background: rgba(255, 68, 68, 0.1);
    color: #ff6b6b;
    border: 1px solid rgba(255, 68, 68, 0.3);
  `}

  ${props => props.type === 'info' && `
    background: rgba(33, 150, 243, 0.1);
    color: #2196f3;
    border: 1px solid rgba(33, 150, 243, 0.3);
  `}
`

const OktaProfileInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`

const OktaProfileBadge = styled.span`
  display: inline-block;
  font-size: 0.8rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  background: #f3e5f5;
  color: #7b1fa2;
`

const EffectiveRole = styled.div`
  background: rgba(33, 150, 243, 0.08);
  border: 1px solid rgba(33, 150, 243, 0.25);
  border-radius: 6px;
  color: ${theme.color.text.base};
  font-size: 0.82rem;
  line-height: 1.45;
  margin: 0.9rem 0 0;
  padding: 0.75rem 0.9rem;

  strong {
    color: #90caf9;
  }
`

const AccessRequestsSection = styled.div`
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #444;
  padding-bottom: 1.5rem;
`

const AccessRequestsTitle = styled.h4`
  margin: 0 0 1rem 0;
  color: #ffc107;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const RequestCard = styled.div`
  background: rgba(255, 193, 7, 0.05);
  border: 1px solid rgba(255, 193, 7, 0.2);
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
`

const RequestInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const RequestName = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${theme.color.text.base};
`

const RequestEmail = styled.div`
  font-size: 0.8rem;
  color: ${theme.color.text.grey};
`

const RequestDate = styled.div`
  font-size: 0.75rem;
  color: ${theme.color.text.grey};
`

const LookupButton = styled.button`
  padding: 0.4rem 0.8rem;
  background: #ffc107;
  color: #333;
  border: none;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #ffca2c;
  }
`

const RequestBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ffc107;
  color: #333;
  font-size: 0.7rem;
  font-weight: 700;
  width: 18px;
  height: 18px;
  border-radius: 50%;
`

const AVAILABLE_GROUPS = [
  { id: '00gv51zanaukHDP7c4x7', name: "Barry's-Admin-Portal-Users" },
  { id: '00gtgjp891m1yr1Ez4x7', name: "Barry's-Admin-Portal-Admins" },
  { id: '00gtgjptvzZzYi1GR4x7', name: "Barry's-Super-Admin" }
]

interface UserData {
  id: string
  email: string
  firstName: string
  lastName: string
  status: string
  groups: { id: string; name: string }[]
  department?: string
  title?: string
}

interface AccessRequest {
  id: string
  name: string
  email: string
  issue: string
  createdAt: string
}

interface RequesterProfile {
  department?: string
  title?: string
}

const UserManagement: React.FC = () => {
  const { user } = useAuth()
  const { logEvent } = useAuditLog()
  const [isOpen, setIsOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [userData, setUserData] = useState<UserData | null>(null)
  const [selectedGroup, setSelectedGroup] = useState('')
  const [isAddingGroup, setIsAddingGroup] = useState(false)
  const [isRemovingGroup, setIsRemovingGroup] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null)
  const [requesterProfile, setRequesterProfile] = useState<RequesterProfile | null>(null)
  const [accessRequests, setAccessRequests] = useState<AccessRequest[]>([])
  const [activeRequestEmail, setActiveRequestEmail] = useState<string | null>(null)

  // Check if the searched user is the current logged-in user
  const isOwnAccount = userData?.email?.toLowerCase() === user?.email?.toLowerCase()

  // Fetch access requests on mount and when modal opens
  useEffect(() => {
    loadAccessRequests()
  }, [])

  useEffect(() => {
    if (isOpen) {
      loadAccessRequests()
    }
  }, [isOpen])

  const loadAccessRequests = async () => {
    try {
      const response = await fetch('/api/access-requests')
      if (response.ok) {
        const data = await response.json()
        setAccessRequests(data.requests || [])
      }
    } catch {
      console.error('Failed to load access requests')
    }
  }

  const handleRequestLookup = (request: AccessRequest) => {
    setEmail(request.email)
    setActiveRequestEmail(request.email)
    // Auto-trigger search
    handleSearchByEmail(request.email)
  }

  const handleSearchByEmail = async (searchEmail: string) => {
    setIsSearching(true)
    setMessage(null)
    setUserData(null)

    try {
      const response = await fetch(`/api/okta/users/lookup?email=${encodeURIComponent(searchEmail)}`)
      const data = await response.json()

      if (response.ok) {
        setUserData(data)
        await logEvent({
          tabName: 'User Management',
          action: AUDIT_ACTIONS.USER_LOOKUP,
          targetUserEmail: data.email,
          details: {
            target_user_name: `${data.firstName} ${data.lastName}`,
            target_user_status: data.status,
            requester_department: requesterProfile?.department,
            requester_title: requesterProfile?.title,
            from_access_request: true
          }
        }, data.email)
      } else {
        setMessage({ type: 'error', text: data.error || 'User not found in Okta. They may need an Okta account first.' })
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to search for user' })
    } finally {
      setIsSearching(false)
    }
  }

  const completeAccessRequest = async (requestId: string) => {
    const response = await fetch(`/api/access-requests/${encodeURIComponent(requestId)}/complete`, { method: 'POST' })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(data.error || 'Failed to complete access request')
    }
    setAccessRequests(prev => prev.filter(r => r.id !== requestId))
    setActiveRequestEmail(null)
  }

  const dismissAccessRequest = async (requestId: string) => {
    try {
      const response = await fetch(`/api/access-requests/${encodeURIComponent(requestId)}`, { method: 'DELETE' })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(data.error || 'Failed to dismiss access request')
      }
      setAccessRequests(prev => prev.filter(r => r.id !== requestId))
      setActiveRequestEmail(null)
    } catch {
      setMessage({ type: 'error', text: 'Failed to dismiss access request' })
    }
  }

  const sendAccessNotification = async (userEmail: string, userName: string, groupName: string) => {
    try {
      const response = await fetch('/api/access-requests/notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail, name: userName, groupName })
      })
      return response.ok
    } catch {
      console.error('Failed to send access notification')
      return false
    }
  }

  const handleCompleteExistingGrant = async () => {
    if (!activeRequestEmail || !userData) return
    if (userData.email.toLowerCase() !== activeRequestEmail.toLowerCase()) {
      setMessage({ type: 'error', text: 'The searched user does not match the pending request.' })
      return
    }

    const matchingRequest = accessRequests.find(
      request => request.email.toLowerCase() === activeRequestEmail.toLowerCase()
    )
    const existingGroup = userData.groups.find(
      userGroup => AVAILABLE_GROUPS.some(group => group.id === userGroup.id)
    )

    if (!matchingRequest || !existingGroup) {
      setMessage({ type: 'error', text: 'The pending request or existing access could not be verified.' })
      return
    }

    setIsAddingGroup(true)
    setMessage(null)
    try {
      await completeAccessRequest(matchingRequest.id)
      setMessage({
        type: 'success',
        text: `Access request completed. ${userData.firstName} ${userData.lastName} already has ${existingGroup.name}. No group change or duplicate notification was sent.`
      })
    } catch {
      setMessage({ type: 'error', text: 'Failed to complete the already-approved access request.' })
    } finally {
      setIsAddingGroup(false)
    }
  }

  // Fetch the logged-in user's Okta profile on mount to get department/title
  useEffect(() => {
    const fetchRequesterProfile = async () => {
      if (!user?.email) return

      try {
        const response = await fetch(`/api/okta/users/lookup?email=${encodeURIComponent(user.email)}`)
        if (response.ok) {
          const data = await response.json()
          setRequesterProfile({
            department: data.department,
            title: data.title
          })
        }
      } catch (error) {
        console.error('Failed to fetch requester Okta profile:', error)
      }
    }

    fetchRequesterProfile()
  }, [user?.email])

  const handleSearch = async () => {
    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Please enter an email address' })
      return
    }

    setIsSearching(true)
    setMessage(null)
    setUserData(null)

    try {
      const response = await fetch(`/api/okta/users/lookup?email=${encodeURIComponent(email)}`)
      const data = await response.json()

      if (response.ok) {
        setUserData(data)

        // Log the user lookup
        await logEvent({
          tabName: 'User Management',
          action: AUDIT_ACTIONS.USER_LOOKUP,
          targetUserEmail: data.email,
          details: {
            target_user_name: `${data.firstName} ${data.lastName}`,
            target_user_status: data.status,
            requester_department: requesterProfile?.department,
            requester_title: requesterProfile?.title
          }
        }, data.email)
      } else {
        setMessage({ type: 'error', text: data.error || 'User not found' })
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to search for user' })
    } finally {
      setIsSearching(false)
    }
  }

  const handleAddGroup = async () => {
    if (!selectedGroup || !userData) return

    setIsAddingGroup(true)
    setMessage(null)

    try {
      const response = await fetch(`/api/okta/groups/${selectedGroup}/users/${userData.id}`, {
        method: 'PUT'
      })
      const data = await response.json()

      if (response.ok) {
        const groupName = AVAILABLE_GROUPS.find(g => g.id === selectedGroup)?.name || selectedGroup

        // Okta records the authoritative group change. A secondary Admin Tool
        // audit failure must remain visible, but it must not leave a granted
        // request stuck in the pending queue.
        let auditLogged = true
        try {
          await logEvent({
            tabName: 'User Management',
            action: AUDIT_ACTIONS.USER_GROUP_ADDED,
            targetUserEmail: userData.email,
            details: {
              group_name: groupName,
              group_id: selectedGroup,
              target_user_name: `${userData.firstName} ${userData.lastName}`,
              requester_department: requesterProfile?.department,
              requester_title: requesterProfile?.title
            }
          }, userData.email)
        } catch {
          auditLogged = false
          console.error('Failed to record Admin Tool access audit event')
        }

        // Complete the access request durably if this approval started from
        // the pending queue. Do not report full success when queue persistence
        // failed after the Okta grant.
        if (activeRequestEmail && activeRequestEmail.toLowerCase() === userData.email.toLowerCase()) {
          const matchingRequest = accessRequests.find(r => r.email.toLowerCase() === activeRequestEmail.toLowerCase())
          if (matchingRequest) {
            try {
              await completeAccessRequest(matchingRequest.id)
            } catch (error) {
              console.error('Failed to complete access request')
              setMessage({
                type: 'error',
                text: `User was added to ${groupName}, but the access request could not be completed. Refresh and retry the pending request.`
              })
              return
            }
          }
        }

        const notificationSent = await sendAccessNotification(
          userData.email,
          `${userData.firstName} ${userData.lastName}`,
          groupName
        )
        const followUps = [
          ...(!auditLogged ? ['the Admin Tool audit entry failed'] : []),
          ...(!notificationSent ? ['the notification email failed'] : [])
        ]
        setMessage({
          type: followUps.length === 0 ? 'success' : 'info',
          text: followUps.length === 0
            ? `User added to ${groupName}. Notification email sent.`
            : `User added to ${groupName}, but ${followUps.join(' and ')}.`
        })

        // Refresh user data (don't log this as another lookup)
        const refreshResponse = await fetch(`/api/okta/users/lookup?email=${encodeURIComponent(email)}`)
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json()
          setUserData(refreshData)
        }
        setSelectedGroup('')
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to add user to group' })
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to add user to group' })
    } finally {
      setIsAddingGroup(false)
    }
  }

  const handleRemoveGroup = async (groupId: string) => {
    if (!userData) return

    setIsRemovingGroup(groupId)
    setMessage(null)

    try {
      const response = await fetch(`/api/okta/groups/${groupId}/users/${userData.id}`, {
        method: 'DELETE'
      })
      const data = await response.json()

      if (response.ok) {
        const groupName = AVAILABLE_GROUPS.find(g => g.id === groupId)?.name || groupId
        setMessage({ type: 'success', text: `User removed from ${groupName}` })

        // Log the group removal
        await logEvent({
          tabName: 'User Management',
          action: AUDIT_ACTIONS.USER_GROUP_REMOVED,
          targetUserEmail: userData.email,
          details: {
            group_name: groupName,
            group_id: groupId,
            target_user_name: `${userData.firstName} ${userData.lastName}`,
            requester_department: requesterProfile?.department,
            requester_title: requesterProfile?.title
          }
        }, userData.email)

        // Refresh user data (don't log this as another lookup)
        const refreshResponse = await fetch(`/api/okta/users/lookup?email=${encodeURIComponent(email)}`)
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json()
          setUserData(refreshData)
        }
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to remove user from group' })
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove user from group' })
    } finally {
      setIsRemovingGroup(null)
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    setEmail('')
    setUserData(null)
    setMessage(null)
    setSelectedGroup('')
  }

  const availableGroupsToAdd = AVAILABLE_GROUPS.filter(
    g => !userData?.groups.some(ug => ug.id === g.id)
  )
  const activeAccessRequest = activeRequestEmail && userData
    && userData.email.toLowerCase() === activeRequestEmail.toLowerCase()
    ? accessRequests.find(request => request.email.toLowerCase() === activeRequestEmail.toLowerCase())
    : null
  const existingPortalGroup = userData?.groups.find(
    userGroup => AVAILABLE_GROUPS.some(group => group.id === userGroup.id)
  )
  const effectiveRole = userData ? portalRoleForGroups(userData.groups) : null

  return (
    <>
      <UserManagementIcon onClick={() => setIsOpen(true)} title="Roles and Access">
        <span style={{ fontSize: '1.1rem' }}>👤</span>
        Roles & Access
        {accessRequests.length > 0 && (
          <IconBadge>{accessRequests.length}</IconBadge>
        )}
      </UserManagementIcon>

      <Modal $isOpen={isOpen} onClick={handleClose}>
        <ModalContent onClick={e => e.stopPropagation()}>
          <ModalHeader>
            <HeaderTitle>Roles & Permissions</HeaderTitle>
            <CloseButton onClick={handleClose}>&times;</CloseButton>
          </ModalHeader>

          <ModalBody>
            <RolePermissions />

            <SectionDivider />

            <AccessHeading>
              <h4>User access</h4>
              <p>Search for a Barry's team member to review or change their assigned Admin Tool role.</p>
            </AccessHeading>

            {accessRequests.length > 0 && (
              <AccessRequestsSection>
                <AccessRequestsTitle>
                  Pending Access Requests <RequestBadge>{accessRequests.length}</RequestBadge>
                </AccessRequestsTitle>
                {accessRequests.map(request => (
                  <RequestCard key={request.id}>
                    <RequestInfo>
                      <RequestName>{request.name}</RequestName>
                      <RequestEmail>{request.email}</RequestEmail>
                      <RequestDate>{new Date(request.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</RequestDate>
                    </RequestInfo>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <LookupButton onClick={() => handleRequestLookup(request)}>
                        Lookup
                      </LookupButton>
                      <LookupButton
                        style={{ background: '#dc3545', color: 'white' }}
                        onClick={() => {
                          if (window.confirm(`Dismiss access request from ${request.name}?`)) {
                            dismissAccessRequest(request.id)
                          }
                        }}
                      >
                        Dismiss
                      </LookupButton>
                    </div>
                  </RequestCard>
                ))}
              </AccessRequestsSection>
            )}

            <FormGroup>
              <Label>Search User by Email</Label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Input
                  type="email"
                  placeholder="user@barrys.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleSearch()}
                />
                <Button variant="primary" onClick={handleSearch} disabled={isSearching}>
                  {isSearching ? 'Searching...' : 'Search'}
                </Button>
              </div>
            </FormGroup>

            {message && (
              <Message type={message.type}>{message.text}</Message>
            )}

            {userData && (
              <UserCard>
                <UserInfo>
                  <UserName>
                    {userData.firstName} {userData.lastName}
                    <UserStatus status={userData.status}>{userData.status}</UserStatus>
                  </UserName>
                  <UserEmail>{userData.email}</UserEmail>
                  {(userData.department || userData.title) && (
                    <OktaProfileInfo>
                      {userData.department && (
                        <OktaProfileBadge>{userData.department}</OktaProfileBadge>
                      )}
                      {userData.title && (
                        <OktaProfileBadge>{userData.title}</OktaProfileBadge>
                      )}
                    </OktaProfileInfo>
                  )}
                  <EffectiveRole>
                    <strong>Effective role: {effectiveRole?.label || 'No Admin Tool access'}</strong>
                    <div>
                      {effectiveRole?.summary || 'Assign a role group to grant Admin Tool access.'}
                    </div>
                  </EffectiveRole>
                </UserInfo>

                {isOwnAccount && (
                  <Message type="error">
                    You cannot modify your own admin portal access. Please contact another super admin to make changes to your account.
                  </Message>
                )}

                <GroupsSection>
                  <GroupsTitle>Current Admin Portal Groups</GroupsTitle>
                  <GroupsList>
                    {userData.groups.length > 0 ? (
                      userData.groups.map(group => (
                        <GroupBadge key={group.id} $isActive>
                          {PORTAL_ROLES.find(role => role.oktaGroup === group.name)?.label || group.name}
                          {!isOwnAccount && (
                            <RemoveGroupButton
                              onClick={() => handleRemoveGroup(group.id)}
                              disabled={isRemovingGroup === group.id}
                              title="Remove from group"
                            >
                              {isRemovingGroup === group.id ? '...' : '×'}
                            </RemoveGroupButton>
                          )}
                        </GroupBadge>
                      ))
                    ) : (
                      <GroupBadge>No admin portal groups assigned</GroupBadge>
                    )}
                  </GroupsList>

                  {activeAccessRequest && existingPortalGroup && (
                    <>
                      <Message type="info">
                        This user already has {existingPortalGroup.name}. Complete the pending request without changing access or sending another notification.
                      </Message>
                      <Button
                        variant="primary"
                        onClick={handleCompleteExistingGrant}
                        disabled={isAddingGroup}
                      >
                        {isAddingGroup ? 'Completing...' : 'Complete Approved Request'}
                      </Button>
                    </>
                  )}

                  {availableGroupsToAdd.length > 0 && !isOwnAccount && (
                    <>
                      <GroupsTitle>Add to Group</GroupsTitle>
                      <AddGroupSection>
                        <Select
                          value={selectedGroup}
                          onChange={e => setSelectedGroup(e.target.value)}
                        >
                          <option value="">Select a group...</option>
                          {availableGroupsToAdd.map(group => (
                            <option key={group.id} value={group.id}>
                              {PORTAL_ROLES.find(role => role.oktaGroup === group.name)?.label || group.name}
                            </option>
                          ))}
                        </Select>
                        <Button
                          variant="primary"
                          onClick={handleAddGroup}
                          disabled={!selectedGroup || isAddingGroup}
                        >
                          {isAddingGroup ? 'Adding...' : 'Add'}
                        </Button>
                      </AddGroupSection>
                    </>
                  )}
                </GroupsSection>
              </UserCard>
            )}

            {!userData && !message && (
              <Message type="info">
                Enter an email address to search for a user and manage their admin portal access.
              </Message>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

export default UserManagement
